import { Badge, Button, Tag, TagCloseButton, TagLabel, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { CpvCodeDto } from "../../autogen/bff-api";
import type { FitsAsMainCode } from "./mainCpvCode";

type Props = {
  selected: string[];
  codeIndex: Record<string, CpvCodeDto>;
  onRemove: (code: string) => void;
  mainCode?: { fits: FitsAsMainCode | null; onMakeMain: (code: string) => void };
};

export const CpvSelectedChips = ({ selected, codeIndex, onRemove, mainCode }: Props) => {
  const { t, i18n } = useTranslation();
  if (selected.length === 0) return null;
  const isNorwegian = i18n.language.startsWith("no");
  return (
    <VStack align="stretch" spacing="1" mb="2">
      {selected.map((code, index) => {
        const dto = codeIndex[code];
        const label = dto ? (isNorwegian ? dto.nameNb : dto.nameEn) : "";
        return (
          <Tag
            key={code}
            colorScheme="teal"
            variant="subtle"
            size="md"
            alignSelf="flex-start"
            maxW="100%"
            data-testid="cpv-chip"
          >
            <TagLabel whiteSpace="normal">
              {code}
              {label ? ` — ${label}` : ""}
            </TagLabel>
            {mainCode && index === 0 && (
              <Badge ml="2" colorScheme="teal" flexShrink="0">
                {t("Main code")}
              </Badge>
            )}
            {mainCode && index > 0 && (
              <MakeMainButton code={code} fits={mainCode.fits ? mainCode.fits(code) : null} onMakeMain={mainCode.onMakeMain} />
            )}
            <TagCloseButton
              aria-label={t("Remove CPV code {{code}}", { code }) ?? undefined}
              onClick={() => onRemove(code)}
            />
          </Tag>
        );
      })}
    </VStack>
  );
};

const MakeMainButton = ({
  code,
  fits,
  onMakeMain,
}: {
  code: string;
  fits: boolean | null;
  onMakeMain: (code: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Button
        ml="2"
        size="xs"
        variant="link"
        colorScheme="teal"
        flexShrink="0"
        isDisabled={fits !== true}
        aria-label={String(t("Set {{code}} as main code", { code }))}
        onClick={() => onMakeMain(code)}
      >
        {t("Set as main code")}
      </Button>
      {fits === false && (
        <Text as="span" ml="1" fontSize="xs" color="smMuted" flexShrink="0">
          {t("Does not fit the contract type")}
        </Text>
      )}
    </>
  );
};
